import React, { useState, useEffect } from 'react';
import { usersAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Users,
  Trash2,
  Shield,
  UserCheck,
  Wrench,
  Loader2,
  Mail,
  Plus,
  UserPlus,
  Edit3,
  Key
} from 'lucide-react';
import Layout from '../../components/Layout';
import { TranslatableSpan } from '../../components/TranslatableText';

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'technician'
  });
  
  // Edit user state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', role: '' });
  const [saving, setSaving] = useState(false);
  
  // Reset password state
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
     
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.update(userId, { role: newRole });
      setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async (userId) => {
    try {
      await usersAPI.delete(userId);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setCreating(true);
    try {
      const response = await usersAPI.create(newUser);
      setUsers([...users, response.data]);
      setNewUser({ name: '', email: '', password: '', role: 'technician' });
      setCreateDialogOpen(false);
      toast.success(`User ${response.data.name} created successfully`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditForm({ name: user.name, email: user.email, role: user.role });
    setEditDialogOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    
    setSaving(true);
    try {
      const response = await usersAPI.update(editingUser.id, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role
      });
      setUsers(users.map(u => u.id === editingUser.id ? { ...u, ...editForm } : u));
      setEditDialogOpen(false);
      setEditingUser(null);
      toast.success('User updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const openResetPasswordDialog = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setResettingPassword(true);
    try {
      await usersAPI.resetPassword(resetPasswordUser.id, newPassword);
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setNewPassword('');
      toast.success(`Password reset for ${resetPasswordUser.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-red-400" />;
      case 'supervisor': return <UserCheck className="w-4 h-4 text-yellow-400" />;
      default: return <Wrench className="w-4 h-4 text-blue-400" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'supervisor': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default: return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <Users className="w-10 h-10 text-amber-500" />
              User Management
            </h1>
            <p className="text-slate-400 mt-1">{users.length} {users.length === 1 ? 'user' : 'users'} in the system</p>
          </div>
          
          {/* Create User Button */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-2" data-testid="create-user-btn">
                <UserPlus className="w-5 h-5" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-amber-500" />
                  Create New User
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Full Name</Label>
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                    className="input-industrial"
                    data-testid="new-user-name"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Email Address</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="user@example.com"
                    className="input-industrial"
                    data-testid="new-user-email"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Password</Label>
                  <Input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    className="input-industrial"
                    data-testid="new-user-password"
                  />
                </div>
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Role</Label>
                  <Select value={newUser.role} onValueChange={(value) => setNewUser(prev => ({ ...prev, role: value }))}>
                    <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="technician">
                        <div className="flex items-center gap-2">
                          <Wrench className="w-4 h-4 text-blue-400" />
                          Technician
                        </div>
                      </SelectItem>
                      <SelectItem value="supervisor">
                        <div className="flex items-center gap-2">
                          <UserCheck className="w-4 h-4 text-yellow-400" />
                          Supervisor
                        </div>
                      </SelectItem>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-red-400" />
                          Administrator
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={() => setCreateDialogOpen(false)}
                    className="btn-secondary flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="submit-create-user"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Create User
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Role Legend */}
        <div className="flex flex-wrap gap-4 p-4 bg-slate-900/50 border border-slate-700 rounded-sm">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-400" />
            <span className="text-slate-400 text-sm">Admin - Full access, manage users & settings</span>
          </div>
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-yellow-400" />
            <span className="text-slate-400 text-sm">Supervisor - Can assign tasks, manage locations</span>
          </div>
          <div className="flex items-center gap-2">
            <Wrench className="w-4 h-4 text-blue-400" />
            <span className="text-slate-400 text-sm">Technician - Can work on assigned/unassigned tasks</span>
          </div>
        </div>

        {/* Users List */}
        <div className="space-y-3">
          {users.map((user) => (
            <div 
              key={user.id}
              className="card-industrial flex flex-col md:flex-row md:items-center gap-4"
              data-testid={`user-${user.id}`}
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 bg-slate-700 rounded-sm flex items-center justify-center">
                  {getRoleIcon(user.role)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium"><TranslatableSpan text={user.name} /></p>
                    {user.id === currentUser?.id && (
                      <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-sm">You</span>
                    )}
                  </div>
                  <p className="text-slate-400 text-sm flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {user.email}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Edit Button */}
                {user.id !== currentUser?.id && (
                  <Button 
                    onClick={() => openEditDialog(user)}
                    className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 border border-blue-500/50 p-2"
                    data-testid={`edit-user-${user.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                )}

                {/* Reset Password Button */}
                {user.id !== currentUser?.id && (
                  <Button 
                    onClick={() => openResetPasswordDialog(user)}
                    className="bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/50 p-2"
                    data-testid={`reset-password-${user.id}`}
                  >
                    <Key className="w-4 h-4" />
                  </Button>
                )}

                {/* Role Selector */}
                {user.id !== currentUser?.id ? (
                  <Select 
                    value={user.role} 
                    onValueChange={(value) => handleRoleChange(user.id, value)}
                  >
                    <SelectTrigger 
                      className={`w-[140px] border ${getRoleColor(user.role)}`}
                      data-testid={`role-select-${user.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="technician">Technician</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className={`px-4 py-2 rounded-sm text-sm uppercase font-mono border ${getRoleColor(user.role)}`}>
                    {user.role}
                  </span>
                )}

                {/* Delete Button */}
                {user.id !== currentUser?.id && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 p-2"
                        data-testid={`delete-user-${user.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete User</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Are you sure you want to delete {user.name}? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleDelete(user.id)} 
                          className="bg-red-500 hover:bg-red-600 text-white"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-blue-400" />
                Edit User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Full Name</Label>
                <Input
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="input-industrial"
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Email Address</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                  className="input-industrial"
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">Role</Label>
                <Select value={editForm.role} onValueChange={(value) => setEditForm(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-white" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="technician">Technician</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  onClick={() => setEditDialogOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="submit-edit-user"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit3 className="w-4 h-4" />}
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent className="bg-slate-900 border-slate-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Key className="w-5 h-5 text-yellow-400" />
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
              <p className="text-slate-400">
                Set a new password for <span className="text-white font-medium">{resetPasswordUser?.name}</span>
              </p>
              <div>
                <Label className="text-slate-400 uppercase text-xs tracking-wider mb-2 block">New Password</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="input-industrial"
                  data-testid="reset-password-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  onClick={() => setResetPasswordDialogOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={resettingPassword || !newPassword}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                  data-testid="submit-reset-password"
                >
                  {resettingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  Reset Password
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default UsersPage;
